import CloseIcon from '@mui/icons-material/Close'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import Login from '@mui/icons-material/Login'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import Refresh from '@mui/icons-material/Refresh'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import Videocam from '@mui/icons-material/Videocam'
import Visibility from '@mui/icons-material/Visibility'
import { alpha } from '@mui/material/styles'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import aumhumLogo from 'src/assets/logo_brand.png'
import {
  checkIntoRoom,
  peekIntoRoom,
  refreshCheckIntoRoom,
} from 'src/clinic/components/CheckIn/services'
import {
  broadcastCheckInUpdate,
  updateCheckInWithValidation,
} from 'src/clinic/components/CheckIn/checkInSyncService'
import { getTeacherChamberRoomScreensStatus } from 'src/consumer/services/featureServices'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import ChamberRoomStatusIndicator from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberRoomStatusIndicator'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  getImageLink,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import {
  REMOTE_CHECKIN_METHOD,
  dedupeEventTypes,
  getCheckInMethodOptions,
  getClinicLocationStatus,
  getDefaultCheckInMethod,
  getPractitionerServicesFromAuthorities,
  getRoomServiceEntries,
  isCheckInMethodEnabled,
} from './MentorClinicAssociationsListView'

dayjs.extend(utc)

const ROOMS_AUTO_REFRESH_MS = 30000

// Visual language mirrored from the associations page's Card View room tiles
// (MentorAssociationClinicSelectionBox): white tile + full-width status band,
// then indigo (emrSecondary) pill actions below.
const BAND_AVAILABLE = { color: 'rgba(34, 139, 84, 0.95)', label: 'Available' }
const BAND_BUSY = { color: 'rgba(212, 58, 112, 0.92)', label: 'Room busy' }
const BAND_MINE = { color: 'rgba(116, 80, 170, 0.95)', label: 'You are checked in' }
const INDIGO = '#6779e3' // theme palette emrSecondary.main

const TEXT_PRIMARY = 'rgba(15, 23, 42, 0.94)'
const TEXT_SECONDARY = 'rgba(71, 85, 105, 0.92)'
const TEXT_TERTIARY = 'rgba(100, 116, 139, 0.8)'

function resolveClinicTimezone(clinicLocationData) {
  const rawTimezone = clinicLocationData?.schedules?.[0]?.timezone
  if (!rawTimezone || typeof rawTimezone !== 'string') {
    return '+05:30'
  }
  if (!rawTimezone.includes('%')) {
    return rawTimezone
  }
  try {
    return decodeURIComponent(rawTimezone)
  } catch {
    return '+05:30'
  }
}

function resolveBrandLogo(clinicLocationData) {
  return getImageLink(
    clinicLocationData?.clinicBrandCoverIcon ||
      clinicLocationData?.clinicBrandIconImage ||
      clinicLocationData?.clinicBrandIcon ||
      clinicLocationData?.coverIcon,
    clinicLocationData?.clinicBrandCoverImage ||
      clinicLocationData?.clinicBrandImage ||
      clinicLocationData?.coverImage,
    false,
  )
}

/** 'online' if any of the room's screens is up, 'offline' if none, 'unknown' if unlisted. */
function getRoomStatusFromScreenStatuses(chamberRoomId, screenStatuses) {
  if (!chamberRoomId || !Array.isArray(screenStatuses) || screenStatuses.length === 0) {
    return 'unknown'
  }
  const roomScreens = screenStatuses.filter((item) => item?.chamberRoomId === chamberRoomId)
  if (roomScreens.length === 0) return 'unknown'
  return roomScreens.some((item) => item?.status === 'up') ? 'online' : 'offline'
}

/** Persist the localStorage state every check-in flow maintains after a successful check-in. */
function persistCheckInSuccess({ emrUsageLog, chamberRoom, roomData, roomEventTypes }) {
  if (emrUsageLog) {
    updateCheckInWithValidation({
      ...emrUsageLog,
      roomName: chamberRoom?.title || roomData?.title,
    })
    broadcastCheckInUpdate(emrUsageLog, chamberRoom || roomData)
  }
  if (chamberRoom) {
    localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
  }
  if (roomEventTypes?.length > 0) {
    localStorage.setItem('roomDataEventTypes', JSON.stringify(roomEventTypes))
  } else {
    localStorage.removeItem('roomDataEventTypes')
  }
}

/**
 * One chamber room on the check-in screen, visually matching the associations page's
 * Card View: a white room tile (title + screen-status indicator + status band, with the
 * occupant strip when busy), then the indigo pill actions below — Check In (contained,
 * one per check-in option, with the In Person/Remotely toggle above when the room
 * offers both) and Peek in / Video calls (outlined). Busy rooms keep Peek in as the
 * pill and expose take-over / request-checkout as small text actions.
 */
function GateRoomCard({
  roomData,
  clinicLocationData,
  rosterGroup,
  screenStatuses,
  onRefreshStatuses,
  onCheckInBusy,
  onAccessGained,
}) {
  const navigate = useNavigate()
  const { mentorProfileData } = useContext(MentorProfileContext) ?? {}
  const [pendingAction, setPendingAction] = useState(null)
  const [refreshingStatus, setRefreshingStatus] = useState(false)

  const methodOptions = useMemo(() => getCheckInMethodOptions(roomData), [roomData])
  const enabledMethodOptions = useMemo(
    () => methodOptions.filter(isCheckInMethodEnabled),
    [methodOptions],
  )
  const preferRemoteByDefault = useMemo(
    () =>
      getPractitionerServicesFromAuthorities().some(
        (serviceConfig) => serviceConfig?.key === 'therapy' || serviceConfig?.key === 'screening',
      ),
    [],
  )
  const [selectedMethod, setSelectedMethod] = useState(() =>
    getDefaultCheckInMethod(methodOptions, null, preferRemoteByDefault),
  )
  const serviceEntries = useMemo(
    () =>
      getRoomServiceEntries(roomData, clinicLocationData?.id, mentorProfileData?.eventTypes || []).filter(
        (serviceEntry) => Boolean(serviceEntry?.checkInOption?.id),
      ),
    [clinicLocationData?.id, mentorProfileData?.eventTypes, roomData],
  )
  const singleService = serviceEntries.length === 1

  const roomOccupied = Boolean(roomData?.occupied)
  const occupiedByMe = roomOccupied && roomData?.checkedInMentorId === mentorProfileData?.id
  const band = occupiedByMe ? BAND_MINE : roomOccupied ? BAND_BUSY : BAND_AVAILABLE
  const rosteredNow = rosterGroup?.status === 'current'
  const canVideoAudit =
    !occupiedByMe && Boolean(roomData?.canVideoAudit && roomData?.activeAuditChannelId)
  // Same rule as the associations page: only rooms with an AUMHUM screen installed have
  // a status worth monitoring.
  const hasScreen = roomData?.hasAUMHUMScreenInstalled === true
  const roomScreenStatus = getRoomStatusFromScreenStatuses(roomData?.id, screenStatuses)

  const allRoomEventTypes = useMemo(
    () => dedupeEventTypes(serviceEntries.flatMap((serviceEntry) => serviceEntry.roomEventTypes || [])),
    [serviceEntries],
  )

  const goToRemoteWorkspace = useCallback(() => {
    navigate(
      `/mentor-home/join-chamber-room/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`,
    )
  }, [clinicLocationData?.clinicBrandId, clinicLocationData?.id, navigate])

  const handleRefreshStatus = async () => {
    if (!onRefreshStatuses) return
    setRefreshingStatus(true)
    try {
      await onRefreshStatuses()
    } finally {
      setRefreshingStatus(false)
    }
  }

  const handleCheckIn = async (serviceEntry) => {
    if (pendingAction) return
    setPendingAction(`checkin:${serviceEntry.key}`)
    try {
      const response = await checkIntoRoom({
        ...roomData,
        clinicBrandId: clinicLocationData?.clinicBrandId,
        clinicLocationId: clinicLocationData?.id,
        selectedCheckinEventOption: serviceEntry.checkInOption.id,
        checkinMethod: selectedMethod,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        persistCheckInSuccess({
          emrUsageLog: response?.data?.data?.['emr-usage-log'],
          chamberRoom: response?.data?.data?.['chamber-room'],
          roomData,
          roomEventTypes:
            serviceEntry.roomEventTypes?.length > 0 ? serviceEntry.roomEventTypes : allRoomEventTypes,
        })
        if (selectedMethod === REMOTE_CHECKIN_METHOD) {
          goToRemoteWorkspace()
        } else {
          // In-person: stay put — the already-loaded calendar is behind the overlay.
          onAccessGained?.()
        }
      } else if (response?.data?.code === 4296) {
        // Actively-occupied room: fall back to the polite checkout-request consent flow.
        onCheckInBusy(roomData)
      }
    } finally {
      setPendingAction(null)
    }
  }

  // Server still shows this practitioner checked in here (e.g. localStorage was cleared):
  // resume by refreshing the check-in instead of creating a new one.
  const handleResume = async () => {
    if (pendingAction) return
    const resumeEntry = serviceEntries[0]
    setPendingAction('resume')
    try {
      const response = await refreshCheckIntoRoom({
        selectedCheckinEventOption: resumeEntry?.checkInOption?.id,
        checkinMethod: selectedMethod,
        clinicBrandId: clinicLocationData?.clinicBrandId,
        clinicLocationId: clinicLocationData?.id,
        chamberRoomId: roomData?.id,
      })
      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        persistCheckInSuccess({
          emrUsageLog: response?.data?.data?.['emr-usage-log'],
          chamberRoom: response?.data?.data?.['chamber-room'],
          roomData,
          roomEventTypes: allRoomEventTypes,
        })
        if (selectedMethod === REMOTE_CHECKIN_METHOD) {
          goToRemoteWorkspace()
        } else {
          onAccessGained?.()
        }
      } else {
        toast.error('Could not resume your check-in. Please try checking in again.')
      }
    } catch (error) {
      console.error('Error resuming check-in:', error)
      toast.error('Could not resume your check-in. Please try checking in again.')
    } finally {
      setPendingAction(null)
    }
  }

  const handlePeekIn = async () => {
    if (pendingAction) return
    setPendingAction('peek')
    try {
      const roomContext = {
        ...roomData,
        clinicBrandId: clinicLocationData?.clinicBrandId || roomData?.clinicBrandId,
        clinicLocationId: clinicLocationData?.id || roomData?.clinicLocationId,
      }
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(roomContext))
      if (allRoomEventTypes.length > 0) {
        localStorage.setItem('roomDataEventTypes', JSON.stringify(allRoomEventTypes))
      } else {
        localStorage.removeItem('roomDataEventTypes')
      }
      try {
        await peekIntoRoom({ ...roomContext, logEventId: 'enter-without-checkin' })
      } catch (error) {
        console.error('Error logging peek-in:', error)
      }
      onAccessGained?.()
    } finally {
      setPendingAction(null)
    }
  }

  // Pill actions matching the Card View's Check In / Peek in buttons.
  const pillSx = {
    width: '100%',
    borderRadius: '16px',
    minHeight: 42,
    textTransform: 'unset',
    fontWeight: 650,
    fontSize: '14.5px',
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {/* Room tile */}
      <Box
        sx={{
          background: occupiedByMe
            ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)'
            : '#fff',
          borderRadius: '10px',
          border: occupiedByMe ? '2px solid rgba(116, 80, 170, 0.5)' : '1px solid #cecece',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: occupiedByMe
            ? '0 6px 20px rgba(116, 80, 170, 0.22)'
            : '0 4px 14px rgba(15, 23, 42, 0.05)',
        }}
      >
        {occupiedByMe && (
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              right: 5,
              backgroundColor: 'rgba(116, 80, 170, 0.95)',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 700,
              px: '6px',
              py: '2px',
              borderRadius: '4px',
              zIndex: 1,
            }}
          >
            CHECKED IN
          </Box>
        )}
        <Box sx={{ p: '10px 12px 8px', display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography
            title={roomData?.title}
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 650,
              fontSize: '15px',
              color: 'rgba(15, 23, 42, 0.9)',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
              pr: occupiedByMe ? '66px' : 0,
            }}
          >
            {roomData?.title || 'Room unit'}
          </Typography>
          {hasScreen && (
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <ChamberRoomStatusIndicator
                status={roomScreenStatus}
                chamberRoomName={roomData?.title}
                compact
              />
              <Tooltip title="Refresh status">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefreshStatus}
                    disabled={refreshingStatus}
                    aria-label="Refresh chamber room status"
                    sx={{
                      padding: '2px',
                      color: 'rgba(116, 80, 170, 0.8)',
                      '&:hover': { backgroundColor: 'rgba(116, 80, 170, 0.08)' },
                    }}
                  >
                    <Refresh
                      sx={{
                        fontSize: 16,
                        animation: refreshingStatus
                          ? 'gate-refresh-spin 0.8s linear infinite'
                          : 'none',
                        '@keyframes gate-refresh-spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        {rosteredNow && !roomOccupied && (
          <Box
            sx={{
              backgroundColor: 'rgba(2, 132, 199, 0.1)',
              color: 'rgba(3, 105, 161, 0.95)',
              fontSize: '12px',
              fontWeight: 650,
              px: '10px',
              py: '3px',
            }}
          >
            Rostered for you right now
          </Box>
        )}
        {roomOccupied && !occupiedByMe && roomData?.checkedInMentorName && (
          <Box sx={{ backgroundColor: '#e5e8eb', fontSize: '12px', px: '10px', py: '3px' }}>
            Present: {roomData.checkedInMentorName}
          </Box>
        )}
        <Box
          sx={{
            backgroundColor: band.color,
            color: '#fff',
            px: '10px',
            py: '5px',
            fontSize: '12px',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {band.label}
        </Box>
      </Box>

      {/* Method toggle — shown when the room offers both In Person and Remotely */}
      {enabledMethodOptions.length > 1 && !occupiedByMe && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${methodOptions.length}, 1fr)`,
            gap: '4px',
            p: '3px',
            borderRadius: '999px',
            backgroundColor: alpha(INDIGO, 0.1),
          }}
        >
          {methodOptions.map((methodOption) => {
            const isSelected = selectedMethod === methodOption?.id
            const methodEnabled = isCheckInMethodEnabled(methodOption)
            return (
              <ButtonBase
                key={methodOption?.id}
                disabled={!methodEnabled || Boolean(pendingAction)}
                onClick={() => setSelectedMethod(methodOption?.id)}
                sx={{
                  py: 0.7,
                  borderRadius: '999px',
                  fontSize: '13px',
                  fontWeight: 650,
                  color: isSelected ? '#fff' : INDIGO,
                  backgroundColor: isSelected ? INDIGO : 'transparent',
                  opacity: methodEnabled ? 1 : 0.4,
                  transition: 'all 0.15s ease',
                }}
              >
                {methodOption?.title}
              </ButtonBase>
            )
          })}
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {occupiedByMe ? (
          <Button
            color="emrSecondary"
            variant="contained"
            disableElevation
            disabled={Boolean(pendingAction)}
            onClick={handleResume}
            startIcon={
              pendingAction === 'resume' ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <Login sx={{ fontSize: 17 }} />
              )
            }
            sx={pillSx}
          >
            {pendingAction === 'resume' ? 'Resuming…' : 'Continue in this room'}
          </Button>
        ) : roomOccupied ? (
          <Button
            color="emrSecondary"
            variant="outlined"
            disabled={Boolean(pendingAction)}
            onClick={handlePeekIn}
            startIcon={
              pendingAction === 'peek' ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <Visibility sx={{ fontSize: 17 }} />
              )
            }
            sx={pillSx}
          >
            Peek in / Video calls
          </Button>
        ) : (
          <>
            {serviceEntries.map((serviceEntry) => {
              const loadingThis = pendingAction === `checkin:${serviceEntry.key}`
              return (
                <Button
                  key={serviceEntry.key}
                  color="emrSecondary"
                  variant="contained"
                  disableElevation
                  disabled={Boolean(pendingAction)}
                  onClick={() => handleCheckIn(serviceEntry)}
                  startIcon={
                    loadingThis ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <Login sx={{ fontSize: 17 }} />
                    )
                  }
                  sx={pillSx}
                >
                  {loadingThis
                    ? 'Checking in…'
                    : singleService
                    ? 'Check In'
                    : `Check In · ${serviceEntry.label}`}
                </Button>
              )
            })}
            <Button
              color="emrSecondary"
              variant="outlined"
              disabled={Boolean(pendingAction)}
              onClick={handlePeekIn}
              startIcon={
                pendingAction === 'peek' ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <Visibility sx={{ fontSize: 17 }} />
                )
              }
              sx={pillSx}
            >
              Peek in / Video calls
            </Button>
          </>
        )}

        {/* Secondary paths for busy rooms + optional video audit */}
        {(canVideoAudit || (roomOccupied && !occupiedByMe)) && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {roomOccupied &&
              !occupiedByMe &&
              serviceEntries.map((serviceEntry) => (
                <Button
                  key={serviceEntry.key}
                  size="small"
                  disabled={Boolean(pendingAction)}
                  onClick={() => handleCheckIn(serviceEntry)}
                  startIcon={
                    pendingAction === `checkin:${serviceEntry.key}` ? (
                      <CircularProgress size={13} color="inherit" />
                    ) : (
                      <Login sx={{ fontSize: 15 }} />
                    )
                  }
                  sx={{
                    textTransform: 'unset',
                    fontWeight: 650,
                    fontSize: '12.5px',
                    color: TEXT_SECONDARY,
                  }}
                >
                  {singleService ? 'Check in & take over' : `Take over · ${serviceEntry.label}`}
                </Button>
              ))}
            {roomOccupied && !occupiedByMe && (
              <Button
                size="small"
                color="warning"
                disabled={Boolean(pendingAction)}
                onClick={() => onCheckInBusy(roomData)}
                sx={{ textTransform: 'unset', fontWeight: 650, fontSize: '12.5px' }}
              >
                Request checkout
              </Button>
            )}
            {canVideoAudit && (
              <Button
                size="small"
                disabled={Boolean(pendingAction)}
                onClick={() => {
                  localStorage.setItem('auditRoomDetails', JSON.stringify(roomData))
                  navigate(
                    `/mentor-home/join-as-auditor/${roomData?.id}/${roomData?.activeAuditChannelId}`,
                  )
                }}
                startIcon={<Videocam sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: 'unset',
                  fontWeight: 650,
                  fontSize: '12.5px',
                  color: TEXT_SECONDARY,
                }}
              >
                Video audit
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

/** Skeleton placeholder shown while the room list loads. */
function GateRoomCardSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          background: '#fff',
          borderRadius: '10px',
          border: '1px solid rgba(15, 23, 42, 0.1)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: '10px 12px' }}>
          <Skeleton variant="text" width="60%" height={24} />
        </Box>
        <Skeleton variant="rectangular" height={28} />
      </Box>
      <Skeleton variant="rounded" height={34} sx={{ borderRadius: '999px' }} />
      <Skeleton variant="rounded" height={42} sx={{ borderRadius: '16px' }} />
      <Skeleton variant="rounded" height={42} sx={{ borderRadius: '16px' }} />
    </Box>
  )
}

/**
 * Full-screen check-in / peek-in screen for the clinic-location calendar page.
 *
 * Shown (variant="gate") when the practitioner opens the page without an active
 * check-in or peek-in at THIS clinic location — instead of the old redirect back to
 * /mentor-home/my-clinic-associations. Light "clinic lobby" layout: a top bar with
 * identity + switch-clinic, a clinic panel (logo, name, hours, live time, and a
 * check-in vs peek-in legend) beside a grid of room tiles that visually match the
 * associations page's Card View. The day's calendar keeps loading underneath, so
 * checking in lands straight on it.
 *
 * Also opened on demand (variant="manual") from the header's "Check In" / "Peeking
 * Privately" chip while peeked in; that variant adds a close button.
 */
export default function ClinicLocationCheckInGate({
  open,
  variant = 'gate',
  onClose,
  clinicLocationData,
  clinicBrandId,
  checkedInDetails: _checkedInDetails,
  activePeekIn,
  // Dev/preview escape hatch: render these rooms instead of fetching (no polling).
  previewRooms = null,
}) {
  const navigate = useNavigate()
  const { mentorProfileData, rosterAssignmentIndex } = useContext(MentorProfileContext) ?? {}
  const [rooms, setRooms] = useState([])
  const [roomsLoadedOnce, setRoomsLoadedOnce] = useState(false)
  const [roomsLoading, setRoomsLoading] = useState(false)
  const [roomsError, setRoomsError] = useState(false)
  const [screenStatuses, setScreenStatuses] = useState([])
  const [requestCheckoutRoom, setRequestCheckoutRoom] = useState(null)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(() => dayjs())
  const fetchSeqRef = useRef(0)

  const clinicLocationId = clinicLocationData?.id
  const clinicTimezone = resolveClinicTimezone(clinicLocationData)
  const clinicCurrentTime = currentTime.utcOffset(clinicTimezone)
  const brandLogo = resolveBrandLogo(clinicLocationData)
  const clinicName = clinicLocationData?.name || clinicLocationData?.title || 'Clinic'
  const brandName = clinicLocationData?.clinicBrandName
  const practitionerName =
    mentorProfileData?.name ||
    [mentorProfileData?.firstName, mentorProfileData?.lastName].filter(Boolean).join(' ') ||
    'Doctor'
  const rosterByRoomId = rosterAssignmentIndex?.byRoomId
  const clinicHours = useMemo(
    () => (clinicLocationData ? getClinicLocationStatus(clinicLocationData) : null),
    [clinicLocationData],
  )

  const fetchRooms = useCallback(
    async ({ silent = false } = {}) => {
      if (!clinicLocationId) {
        return
      }
      const fetchSeq = ++fetchSeqRef.current
      if (!silent) {
        setRoomsLoading(true)
      }
      try {
        const response = await getMPDataWithParams('/api/secure/teacher/associated-chamber-rooms', {
          clinicLocationId,
          ...(clinicBrandId ? { clinicBrandId } : {}),
        })
        if (fetchSeq !== fetchSeqRef.current) {
          return
        }
        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          const definedAccessRooms = response?.data?.data?.['defined-room-access'] || []
          const impliedAccessRooms = response?.data?.data?.['implied-room-access'] || []
          const seenRoomIds = new Set()
          const mergedRooms = [...definedAccessRooms, ...impliedAccessRooms].filter((room) => {
            if (!room?.id || seenRoomIds.has(room.id)) return false
            seenRoomIds.add(room.id)
            return true
          })
          setRooms(mergedRooms)
          setRoomsLoadedOnce(true)
          setRoomsError(false)
        } else if (!silent) {
          setRoomsError(true)
        }
      } catch (error) {
        console.error('Error loading chamber rooms for check-in gate:', error)
        if (fetchSeq === fetchSeqRef.current && !silent) {
          setRoomsError(true)
        }
      } finally {
        if (fetchSeq === fetchSeqRef.current && !silent) {
          setRoomsLoading(false)
        }
      }
    },
    [clinicBrandId, clinicLocationId],
  )

  // AUMHUM-screen online/offline statuses for the room tiles (same indicator as the
  // associations page). Best-effort — the service returns [] on any failure.
  const fetchScreenStatuses = useCallback(async () => {
    const statuses = await getTeacherChamberRoomScreensStatus()
    if (Array.isArray(statuses)) {
      setScreenStatuses(statuses)
    }
  }, [])

  // Load rooms when the gate opens (or the location changes while open), then keep
  // occupancy fresh with a silent background refresh.
  useEffect(() => {
    if (!open || !clinicLocationId) {
      return undefined
    }
    if (previewRooms) {
      setRooms(previewRooms)
      setRoomsLoadedOnce(true)
      return undefined
    }
    setRooms([])
    setRoomsLoadedOnce(false)
    fetchRooms()
    fetchScreenStatuses()
    const intervalId = setInterval(() => fetchRooms({ silent: true }), ROOMS_AUTO_REFRESH_MS)
    return () => clearInterval(intervalId)
  }, [open, clinicLocationId, fetchRooms, fetchScreenStatuses, previewRooms])

  // Clinic-local time — minute precision is all the display needs.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    setCurrentTime(dayjs())
    const intervalId = setInterval(() => setCurrentTime(dayjs()), 15000)
    return () => clearInterval(intervalId)
  }, [open])

  // Own active room first, then rooms rostered right now, then free rooms, then busy —
  // the primary action stays on top, which matters most on phones.
  const sortedRooms = useMemo(() => {
    const rank = (room) => {
      if (room?.occupied && room?.checkedInMentorId === mentorProfileData?.id) return 0
      if (rosterByRoomId?.[room?.id]?.status === 'current') return 1
      if (!room?.occupied) return 2
      return 3
    }
    return [...rooms].sort((roomA, roomB) => rank(roomA) - rank(roomB))
  }, [rooms, mentorProfileData?.id, rosterByRoomId])

  const handleSubmitCheckoutRequest = async (reason) => {
    setRequestCheckoutLoading(true)
    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: requestCheckoutRoom?.id,
        reason,
      })
      if (request) {
        setRequestCheckoutRoom(null)
        toast.success(
          `Checkout request sent to ${requestCheckoutRoom?.checkedInMentorName || 'the checked-in practitioner'}.`,
        )
        return
      }
      toast.error(getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'))
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }

  const isManual = variant === 'manual'
  const peekedSince = activePeekIn?.peekedInAt
    ? dayjs.utc(activePeekIn.peekedInAt).utcOffset(clinicTimezone).format('h:mm A')
    : null

  return (
    <Dialog
      open={Boolean(open)}
      fullScreen
      disableEscapeKeyDown={!isManual}
      onClose={isManual ? onClose : undefined}
      PaperProps={{
        sx: {
          background:
            'radial-gradient(circle at 12% 0%, rgba(103, 121, 227, 0.07), transparent 34%), radial-gradient(circle at 92% 8%, rgba(16, 185, 129, 0.06), transparent 36%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
          color: TEXT_PRIMARY,
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Top bar: product mark · signed-in identity · switch clinic / close */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2, sm: 3, md: 4 },
            py: 1.5,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(8px)',
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          <Box
            component="img"
            src={aumhumLogo}
            alt="AUMHUM"
            sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: '10px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.6,
                borderRadius: '999px',
                backgroundColor: alpha(INDIGO, 0.08),
                border: `1px solid ${alpha(INDIGO, 0.2)}`,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: INDIGO,
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {practitionerName.replace(/^Dr\.?\s*/i, '').charAt(0).toUpperCase() || 'D'}
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 220,
                }}
              >
                {practitionerName}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/mentor-home/my-clinic-associations')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '13px',
                borderRadius: '999px',
                color: TEXT_SECONDARY,
                borderColor: 'rgba(15, 23, 42, 0.18)',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: 'rgba(15, 23, 42, 0.35)', backgroundColor: 'rgba(15, 23, 42, 0.03)' },
              }}
            >
              Switch clinic
            </Button>
            {isManual && (
              <IconButton
                aria-label="Close check-in screen"
                onClick={onClose}
                size="small"
                sx={{
                  color: TEXT_SECONDARY,
                  border: '1px solid rgba(15, 23, 42, 0.14)',
                  '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.05)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Body: clinic panel + room grid */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '1440px',
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr)' },
            gap: { xs: 2.5, md: 5 },
            alignItems: 'start',
            flex: 1,
          }}
        >
          {/* Clinic identity panel */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              {brandLogo ? (
                <Box
                  component="img"
                  src={brandLogo}
                  alt={brandName || clinicName}
                  sx={{
                    width: { xs: 56, md: 68 },
                    height: { xs: 56, md: 68 },
                    objectFit: 'cover',
                    borderRadius: '16px',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                    backgroundColor: '#fff',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: 56, md: 68 },
                    height: { xs: 56, md: 68 },
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(103, 121, 227, 0.12) 0%, rgba(16, 185, 129, 0.1) 100%)',
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    flexShrink: 0,
                  }}
                >
                  <LocationOnIcon sx={{ color: INDIGO, fontSize: 30 }} />
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                {brandName && (
                  <Typography
                    sx={{
                      fontSize: '12.5px',
                      fontWeight: 700,
                      color: TEXT_TERTIARY,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {brandName}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: { xs: '21px', md: '24px' },
                    fontWeight: 800,
                    color: TEXT_PRIMARY,
                    lineHeight: 1.2,
                  }}
                >
                  {clinicName}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {clinicHours?.label && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    px: 1.25,
                    py: 0.45,
                    borderRadius: '999px',
                    backgroundColor: alpha(clinicHours.accent || '#64748b', 0.1),
                    border: `1px solid ${alpha(clinicHours.accent || '#64748b', 0.24)}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: clinicHours.accent || '#94a3b8',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '12.5px',
                      fontWeight: 700,
                      color: clinicHours.accent || TEXT_SECONDARY,
                    }}
                  >
                    {clinicHours.label}
                  </Typography>
                </Box>
              )}
              <Typography sx={{ fontSize: '13px', fontWeight: 650, color: TEXT_TERTIARY }}>
                {clinicCurrentTime.format('ddd, D MMM')} · {clinicCurrentTime.format('h:mm A')}
              </Typography>
            </Box>

            {/* Peek-in status (manual variant) */}
            {isManual && peekedSince && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  p: 1.5,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(217, 119, 6, 0.08)',
                  border: '1px solid rgba(217, 119, 6, 0.2)',
                }}
              >
                <Visibility sx={{ fontSize: 18, color: '#b45309', mt: '1px' }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 650, color: '#92400e', lineHeight: 1.45 }}>
                  Peeking privately since {peekedSince}. Check in to go on record.
                </Typography>
              </Box>
            )}

            {/* What the two actions mean — the 10-second explainer */}
            <Box
              sx={{
                mt: 2.5,
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                gap: 1.5,
                p: 2,
                borderRadius: '14px',
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    backgroundColor: alpha(INDIGO, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Login sx={{ fontSize: 17, color: INDIGO }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13.5px', fontWeight: 750, color: TEXT_PRIMARY }}>
                    Check In
                  </Typography>
                  <Typography sx={{ fontSize: '12.5px', color: TEXT_SECONDARY, lineHeight: 1.45 }}>
                    Take the room and start your clinic day on record — in person or remotely.
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(100, 116, 139, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Visibility sx={{ fontSize: 17, color: '#475569' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13.5px', fontWeight: 750, color: TEXT_PRIMARY }}>
                    Peek in
                  </Typography>
                  <Typography sx={{ fontSize: '12.5px', color: TEXT_SECONDARY, lineHeight: 1.45 }}>
                    View the calendar and attend video calls without taking the room.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography
              sx={{
                mt: 2,
                display: { xs: 'none', md: 'block' },
                fontSize: '12px',
                color: TEXT_TERTIARY,
                lineHeight: 1.5,
              }}
            >
              Your day’s calendar is already loading behind this screen — you’ll land on it
              the moment you’re in.
            </Typography>
          </Box>

          {/* Room grid */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 800, color: TEXT_PRIMARY }}>
                Choose your chamber room
              </Typography>
              <Tooltip title="Refresh room availability">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      fetchRooms()
                      fetchScreenStatuses()
                    }}
                    disabled={roomsLoading}
                    sx={{ color: TEXT_TERTIARY }}
                  >
                    <Refresh
                      sx={{
                        fontSize: 20,
                        animation: roomsLoading ? 'gate-refresh-spin 0.8s linear infinite' : 'none',
                        '@keyframes gate-refresh-spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: '13.5px', color: TEXT_SECONDARY, mb: 2 }}>
              Check in to take a room, or peek in to work alongside without taking it.
            </Typography>

            {!roomsLoadedOnce && (roomsLoading || (!roomsError && !previewRooms)) ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(240px, 1fr))' },
                  gap: { xs: 1.75, sm: 2.25 },
                  maxWidth: '760px',
                }}
              >
                <GateRoomCardSkeleton />
                <GateRoomCardSkeleton />
              </Box>
            ) : sortedRooms.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 420px)',
                    sm: 'repeat(auto-fill, minmax(240px, 300px))',
                  },
                  alignItems: 'start',
                  gap: { xs: 1.75, sm: 2.25 },
                }}
              >
                {sortedRooms.map((roomData) => (
                  <GateRoomCard
                    key={roomData.id}
                    roomData={roomData}
                    clinicLocationData={clinicLocationData}
                    rosterGroup={rosterByRoomId?.[roomData.id] || null}
                    screenStatuses={screenStatuses}
                    onRefreshStatuses={async () => {
                      await Promise.all([fetchRooms({ silent: true }), fetchScreenStatuses()])
                    }}
                    onCheckInBusy={(busyRoom) => setRequestCheckoutRoom(busyRoom)}
                    // Manual variant closes as soon as access is secured; the blocking
                    // variant dismisses itself via the page's access poll.
                    onAccessGained={isManual ? onClose : undefined}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 5,
                  px: 2,
                  maxWidth: '480px',
                  borderRadius: '16px',
                  border: '1px dashed rgba(15, 23, 42, 0.18)',
                  background: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                <MeetingRoomIcon sx={{ fontSize: 38, color: TEXT_TERTIARY, mb: 1 }} />
                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 700 }}>
                  {roomsError
                    ? 'Could not load the chamber rooms for this clinic.'
                    : 'No chamber rooms are assigned to you at this clinic location.'}
                </Typography>
                <Typography sx={{ color: TEXT_SECONDARY, fontSize: '13px', mt: 0.5 }}>
                  {roomsError
                    ? 'Check your connection and try again.'
                    : 'Ask the clinic team to add you to a room, or switch to another clinic.'}
                </Typography>
                {roomsError && (
                  <Button
                    onClick={() => fetchRooms()}
                    startIcon={<Refresh />}
                    variant="outlined"
                    sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Retry
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <CheckinRequestPromptDialog
        open={Boolean(requestCheckoutRoom)}
        roomData={requestCheckoutRoom}
        clinicLocationData={clinicLocationData}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutRoom(null)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </Dialog>
  )
}
